import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { Mail, MessagesSquare, Pencil, Reply } from "lucide-react";

import UpcomingPill from "@/components/communications/UpcomingPill";
import { isUpcomingItem } from "@/components/communications/threadFormat";
import { Error as LoadError } from "@/components/shared/Error";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { useChannelAvailability } from "@/hooks/useChannelAvailability";
import useLiveResource from "@/hooks/useLiveResource";
import { CommunicationsService } from "@/services/communicationsService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

import ComposeModal from "./components/ComposeModal";
import EmailMessage from "./components/EmailMessage";
import ReplyBox from "./components/ReplyBox";
import ThreadList from "./components/ThreadList";

/** Rows of the shape the real ones take, so the panel does not jump when they arrive. */
const PaneSkeleton = ({ label }) => (
  <div role="status" aria-label={label} className="space-y-3 p-4">
    <div className="h-6 w-2/3 animate-pulse rounded bg-background-tertiary" />
    {[0, 1, 2].map((row) => (
      <div key={row} className="h-20 animate-pulse rounded-lg bg-background-tertiary" />
    ))}
  </div>
);

const replySubject = (subject) => {
  const base = (subject || "").trim();
  if (!base) return "Re:";
  return /^re:/i.test(base) ? base : `Re: ${base}`;
};

/**
 * The Email tab on a candidate, scoped to the application on screen.
 *
 * It reads like a mail client, by decision — conversation list, reading pane,
 * Reply under the thread, a Compose window — because that is the shape every
 * recruiter already knows from Gmail and Outlook.
 *
 * `/admin/communications` is the other half and is deliberately not replaced
 * by it: that screen is candidate-scoped and carries WhatsApp and Telegram as
 * well, and the footer link hands the recruiter over to it for those.
 *
 * Threads are keyed by `thread_id`: a sequence is one conversation, and each
 * ad-hoc email the recruiter starts is another. The most recent one opens on
 * mount. A `conversation` event naming this candidate refetches both panes
 * silently, so a reply lands without the recruiter leaving the tab.
 *
 * No attachment control anywhere in this tree, by decision (§1.4).
 */
const CandidateEmail = ({ candidateId, applicationId = null, candidateDetails }) => {
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState(null);
  const [threadReload, setThreadReload] = useState(0);
  const [composing, setComposing] = useState(false);
  const [replying, setReplying] = useState(false);
  // Per-message fold state the recruiter chose; anything not in here takes
  // the default (newest and undelivered open, the rest folded).
  const [toggled, setToggled] = useState({});

  const { canEmail } = useChannelAvailability(candidateDetails);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // The newest request wins: a thread landing after a newer one went out would
  // paint the conversation the recruiter just switched away from.
  const threadRequestRef = useRef(0);
  const silentThreadRef = useRef(false);

  const loadThreads = useCallback(
    async ({ silent = false, selectNewest = false } = {}) => {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const data = await CommunicationsService.listThreads(candidateId, { applicationId });
        if (!mountedRef.current) return;
        const items = data?.items ?? [];
        setThreads(items);
        setError(null);
        // Newest-first, so the first row moved last. Keep the open thread if it
        // is still listed — a refresh must not throw the reader back to the top.
        setActiveThreadId((current) => {
          if (!selectNewest && current && items.some((t) => t.thread_id === current)) return current;
          return items[0]?.thread_id ?? null;
        });
      } catch (err) {
        if (!mountedRef.current) return;
        // A failed background refresh keeps what is on screen; only a load the
        // recruiter is waiting on turns into the error screen.
        if (!silent) setError(apiErrorMessageFormat(err, "We couldn't load this candidate's email."));
      } finally {
        if (mountedRef.current && !silent) setLoading(false);
      }
    },
    [candidateId, applicationId],
  );

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  // A different conversation starts folded to its defaults, with no reply open.
  useEffect(() => {
    setToggled({});
    setReplying(false);
  }, [activeThreadId]);

  useEffect(() => {
    if (!activeThreadId) {
      setThread(null);
      return;
    }
    const silent = silentThreadRef.current;
    silentThreadRef.current = false;
    const requestId = threadRequestRef.current + 1;
    threadRequestRef.current = requestId;
    if (!silent) {
      setThreadLoading(true);
      setThreadError(null);
    }
    (async () => {
      try {
        const data = await CommunicationsService.getThread(candidateId, activeThreadId, { applicationId });
        if (!mountedRef.current || threadRequestRef.current !== requestId) return;
        setThread(data);
        setThreadError(null);
      } catch (err) {
        if (!mountedRef.current || threadRequestRef.current !== requestId) return;
        if (!silent) setThreadError(apiErrorMessageFormat(err, "We couldn't open this conversation."));
      } finally {
        if (mountedRef.current && threadRequestRef.current === requestId) setThreadLoading(false);
      }
    })();
  }, [candidateId, applicationId, activeThreadId, threadReload]);

  const refreshSilently = useCallback(() => {
    loadThreads({ silent: true });
    silentThreadRef.current = true;
    setThreadReload((n) => n + 1);
  }, [loadThreads]);

  useLiveResource({
    keys: "conversation",
    enabled: Boolean(candidateId),
    match: (event) => String(event?.id) === String(candidateId),
    onChange: refreshSilently,
  });

  const activeThread = threads.find((t) => t.thread_id === activeThreadId) || null;
  const to = thread?.contact?.email || candidateDetails?.email || "";
  const contactName = thread?.contact?.full_name || candidateDetails?.full_name;

  const handleNewEmail = async ({ subject, body }) => {
    try {
      await CommunicationsService.send({ candidateId, applicationId, threadId: undefined, subject, body });
      setComposing(false);
      toast.success("Email sent");
      await loadThreads({ silent: true, selectNewest: true });
    } catch (err) {
      toast.error(apiErrorMessageFormat(err, "We couldn't send this email."));
    }
  };

  const handleReply = async (body) => {
    try {
      await CommunicationsService.send({
        candidateId,
        applicationId,
        threadId: activeThreadId,
        subject: replySubject(activeThread?.subject),
        body,
      });
      setReplying(false);
      toast.success("Reply sent");
      refreshSilently();
      return true;
    } catch (err) {
      toast.error(apiErrorMessageFormat(err, "We couldn't send this reply."));
      return false;
    }
  };

  // The AI writes from the recruiter's note or improves their text; a reply
  // is written against the thread it answers, and keeps that thread's subject.
  const draftNewEmail = (instruction, { subject, body }) =>
    CommunicationsService.draft({ candidateId, applicationId, channel: "email", instruction, subject, body });
  const draftReply = (instruction, { body }) =>
    CommunicationsService.draft({ candidateId, applicationId, channel: "email", threadId: activeThreadId, instruction, body });

  if (loading) return <PaneSkeleton label="Loading email" />;
  if (error) return <LoadError error={error} onHandleRetry={() => loadThreads()} />;

  const items = thread?.items ?? [];
  const messages = items.filter((it) => !isUpcomingItem(it));
  const upcoming = items.filter(isUpcomingItem);
  const latestId = messages[messages.length - 1]?.id;
  const isExpanded = (it) => {
    const key = `${it.kind}-${it.id}`;
    if (key in toggled) return toggled[key];
    return it.id === latestId || (it.direction === "outbound" && ["failed", "bounced"].includes(it.status));
  };
  const toggle = (it) => {
    const key = `${it.kind}-${it.id}`;
    setToggled((prev) => ({ ...prev, [key]: !isExpanded(it) }));
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        {/* Inbox column */}
        <aside className="flex max-h-64 shrink-0 flex-col border-b border-border-primary md:max-h-none md:w-80 md:border-b-0 md:border-r">
          <div className="flex items-center gap-2 p-3">
            <Button type="button" variant="secondary" disabled={!canEmail} onClick={() => setComposing(true)}>
              <Pencil aria-hidden="true" />
              New email
            </Button>
          </div>
          {!canEmail && <p className="px-4 pb-3 text-xs text-text-secondary">{candidateDetails?.full_name || "This candidate"} has no email address on file.</p>}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {threads.length === 0 ? (
              <p className="px-4 py-6 text-sm text-text-secondary">No email has been sent for this application yet.</p>
            ) : (
              <ThreadList threads={threads} value={activeThreadId} onChange={setActiveThreadId} />
            )}
          </div>
        </aside>

        {/* Reading pane */}
        <section aria-label="Conversation" className="min-h-0 flex-1 overflow-y-auto">
          {threads.length === 0 ? (
            <div className="flex h-full items-center justify-center p-6">
              <div className="flex max-w-sm flex-col items-center gap-2 text-center">
                <Mail size={24} aria-hidden="true" className="text-text-secondary" />
                <p className="text-sm text-text-primary">Write to {candidateDetails?.full_name || "this candidate"} about this job.</p>
                <p className="text-xs text-text-secondary">Replies land here, threaded under the email they answer.</p>
              </div>
            </div>
          ) : threadError ? (
            <LoadError error={threadError} onHandleRetry={() => setThreadReload((n) => n + 1)} />
          ) : threadLoading && !thread ? (
            <PaneSkeleton label="Loading conversation" />
          ) : (
            <div className="mx-auto flex max-w-3xl flex-col gap-3 p-4">
              <div className="flex flex-wrap items-center gap-2 pb-1">
                <h2 className="text-lg font-semibold text-text-primary">{activeThread?.subject || "(no subject)"}</h2>
                {activeThread?.sequence_name && (
                  <span className="rounded-full bg-background-tertiary px-2 py-0.5 text-xs text-text-secondary">{activeThread.sequence_name}</span>
                )}
              </div>

              {messages.length > 0 && (
                <div className="divide-y divide-border-primary overflow-hidden rounded-lg border border-border-primary bg-background-secondary">
                  {messages.map((it) => (
                    <EmailMessage key={`${it.kind}-${it.id}`} item={it} contactName={contactName} expanded={isExpanded(it)} onToggle={() => toggle(it)} />
                  ))}
                </div>
              )}
              {upcoming.map((it) => (
                <UpcomingPill key={`${it.kind}-${it.id}`} item={it} />
              ))}

              {replying ? (
                <ReplyBox to={to} onSend={handleReply} onDraft={draftReply} onDiscard={() => setReplying(false)} />
              ) : (
                <div>
                  <Button type="button" variant="outline" size="lg" disabled={!canEmail} onClick={() => setReplying(true)} className="rounded-full px-4">
                    <Reply aria-hidden="true" />
                    Reply
                  </Button>
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      <div className="border-t border-border-primary px-4 py-2.5">
        <Link to={`/admin/communications?candidate=${candidateId}`} className="inline-flex items-center gap-1.5 text-xs text-text-secondary hover:text-text-primary">
          <MessagesSquare size={13} aria-hidden="true" />
          WhatsApp and Telegram live in Communications
        </Link>
      </div>

      <ComposeModal open={composing} to={to} onClose={() => setComposing(false)} onSend={handleNewEmail} onDraft={draftNewEmail} />
    </div>
  );
};

export default CandidateEmail;
